import { createRouter, createWebHistory } from 'vue-router'
import { auth, db } from '@/firebase/config'
import { doc, getDoc } from 'firebase/firestore'
import { USER_ROLES } from '@/utils/roles'

// Import views
import Home from '@/views/Home.vue'
import Login from '@/views/auth/Login.vue'
import Register from '@/views/auth/Register.vue'
import Fitness from '@/views/RegularUser/Fitness.vue'
import HealthConsultation from '@/views/RegularUser/HealthConsultation.vue'
import Community from '@/views/RegularUser/Community.vue'
import Events from '@/views/Events.vue'
import Profile from '@/views/Profile.vue'
import Settings from '@/views/Settings.vue'
import NotFound from '@/views/NotFound.vue'

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
    meta: { title: "FemWellness - Your Complete Women's Health Journey" },
  },
  {
    path: '/fitness',
    name: 'Fitness',
    component: Fitness,
    meta: { title: 'Fitness & Lifestyle - FemWellness' },
  },
  {
    path: '/health-consultation',
    name: 'HealthConsultation',
    component: HealthConsultation,
    meta: { title: 'Health Consultation - FemWellness' },
  },
  {
    path: '/community',
    name: 'Community',
    component: Community,
    meta: { title: 'Community - FemWellness' },
  },
  {
    path: '/community/post/:id',
    name: 'PostDetail',
    component: () => import('@/views/RegularUser/PostDetail.vue'),
    meta: { title: 'Post Detail - FemWellness' },
  },
  {
    path: '/events',
    name: 'Events',
    component: Events,
    meta: { title: 'Events - FemWellness' },
  },
  {
    path: '/events/:id',
    name: 'EventDetail',
    component: () => import('@/views/event/EventDetail.vue'),
    meta: { title: 'Event Details - FemWellness' },
  },
  {
    path: '/events/calendar',
    name: 'EventCalendar',
    component: () => import('@/views/event/CalendarView.vue'),
    meta: { title: 'Event Calendar - FemWellness' },
  },
  {
    path: '/my-events',
    name: 'MyEvents',
    component: () => import('@/views/MyEvents.vue'),
    meta: { title: 'My Events - FemWellness', requiresAuth: true },
  },
  {
    path: '/profile',
    name: 'Profile',
    component: Profile,
    meta: { title: 'Profile - FemWellness', requiresAuth: true },
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { title: 'Login - FemWellness' },
  },
  {
    path: '/register',
    name: 'Register',
    component: Register,
    meta: { title: 'Register - FemWellness' },
  },
  {
    path: '/settings',
    name: 'Settings',
    component: Settings,
    meta: { title: 'Settings - FemWellness', requiresAuth: true },
  },

  // Doctor Routes
  {
    path: '/doctor',
    name: 'DoctorDashboard',
    component: () => import('@/views/doctor/Dashboard.vue'),
    meta: {
      title: 'Doctor Dashboard - FemWellness',
      requiresAuth: true,
      requiresRole: USER_ROLES.DOCTOR,
    },
  },
  {
    path: '/doctor/patients',
    name: 'PatientManagement',
    component: () => import('@/views/doctor/PatientManagement.vue'),
    meta: {
      title: 'Patient Management - FemWellness',
      requiresAuth: true,
      requiresRole: USER_ROLES.DOCTOR,
    },
  },
  {
    path: '/doctor/consultations',
    name: 'Consultations',
    component: () => import('@/views/doctor/Consultations.vue'),
    meta: {
      title: 'Consultations - FemWellness',
      requiresAuth: true,
      requiresRole: USER_ROLES.DOCTOR,
    },
  },
  {
    path: '/doctor/content',
    name: 'ContentCreator',
    component: () => import('@/views/doctor/ContentCreator.vue'),
    meta: {
      title: 'Content Creator - FemWellness',
      requiresAuth: true,
      requiresRole: USER_ROLES.DOCTOR,
    },
  },
  {
    path: '/doctor/events',
    name: 'DoctorEvents',
    component: () => import('@/views/doctor/EventManagement.vue'),
    meta: {
      title: 'Event Management - FemWellness',
      requiresAuth: true,
      requiresRole: USER_ROLES.DOCTOR,
    },
  },
  {
    path: '/doctor/profile',
    name: 'DoctorProfile',
    component: () => import('@/views/doctor/Profile.vue'),
    meta: {
      title: 'Doctor Profile - FemWellness',
      requiresAuth: true,
      requiresRole: USER_ROLES.DOCTOR,
    },
  },

  // Admin Routes
  {
    path: '/admin',
    name: 'AdminDashboard',
    component: () => import('@/views/admin/Dashboard.vue'),
    meta: {
      title: 'Admin Dashboard - FemWellness',
      requiresAuth: true,
      requiresRole: USER_ROLES.ADMIN,
    },
  },
  {
    path: '/admin/users',
    name: 'UserManagement',
    component: () => import('@/views/admin/UserManagement.vue'),
    meta: {
      title: 'User Management - FemWellness',
      requiresAuth: true,
      requiresRole: USER_ROLES.ADMIN,
    },
  },
  {
    path: '/admin/content',
    name: 'ContentModeration',
    component: () => import('@/views/admin/ContentModeration.vue'),
    meta: {
      title: 'Content Moderation - FemWellness',
      requiresAuth: true,
      requiresRole: USER_ROLES.ADMIN,
    },
  },
  {
    path: '/admin/events',
    name: 'AdminEvents',
    component: () => import('@/views/admin/EventManagement.vue'),
    meta: {
      title: 'Event Management - FemWellness',
      requiresAuth: true,
      requiresRole: USER_ROLES.ADMIN,
    },
  },
  {
    path: '/admin/settings',
    name: 'SystemSettings',
    component: () => import('@/views/admin/SystemSettings.vue'),
    meta: {
      title: 'System Settings - FemWellness',
      requiresAuth: true,
      requiresRole: USER_ROLES.ADMIN,
    },
  },
  {
    path: '/admin/analytics',
    name: 'Analytics',
    component: () => import('@/views/admin/Analytics.vue'),
    meta: {
      title: 'Analytics - FemWellness',
      requiresAuth: true,
      requiresRole: USER_ROLES.ADMIN,
    },
  },
  {
    path: '/admin/patients-table',
    name: 'PatientsTable',
    component: () => import('@/views/admin/PatientsTable.vue'),
    meta: {
      title: 'Patient Management - FemWellness',
      requiresAuth: true,
      requiresRole: USER_ROLES.ADMIN,
    },
  },
  {
    path: '/admin/health-records',
    name: 'HealthRecordsTable',
    component: () => import('@/views/admin/HealthRecordsTable.vue'),
    meta: {
      title: 'Health Records - FemWellness',
      requiresAuth: true,
      requiresRole: USER_ROLES.ADMIN,
    },
  },

  // 404 Not Found
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFound,
    meta: { title: 'Page Not Found - FemWellness' },
  },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  },
})

// Helper function to get user role
async function getUserRole(user) {
  if (!user) return null

  try {
    const userDoc = await getDoc(doc(db, 'users', user.uid))
    if (userDoc.exists()) {
      return userDoc.data().role
    }
  } catch (error) {
    console.error('Error getting user role:', error)
  }

  return null
}

// Navigation Guards
router.beforeEach(async (to, from, next) => {
  // Set page title
  if (to.meta.title) {
    document.title = to.meta.title
  }

  // Check authentication with Firebase
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    const user = auth.currentUser
    if (!user) {
      next({
        path: '/login',
        query: { redirect: to.fullPath },
      })
      return
    }

    // Check role-based access
    if (to.matched.some((record) => record.meta.requiresRole)) {
      const userRole = await getUserRole(user)
      const requiredRole = to.meta.requiresRole

      if (!userRole || userRole !== requiredRole) {
        // Redirect based on user role or to home if no role
        if (userRole === USER_ROLES.ADMIN) {
          next('/admin')
        } else if (userRole === USER_ROLES.DOCTOR) {
          next('/doctor')
        } else {
          next('/')
        }
        return
      }
    }

    next()
  } else {
    next()
  }
})

export default router
